import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import {
  Home,
  Calendar,
  Users,
  Briefcase,
  MessageSquare,
  MessagesSquare,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  Sparkles,
  Shield,
  Bot,
  ChevronDown,
  HelpCircle,
  ArrowLeft,
  Search,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { cn } from '../lib/utils';

// Define root pages (no back button needed)
const ROOT_PAGES = ['/dashboard', '/operations', '/my-day', '/calendar', '/bookings', '/clients', '/chat', '/chats', '/assistant', '/admin', '/settings', '/notifications', '/property-lookup'];

// Fallback routes for back navigation
const FALLBACK_ROUTES = {
  '/bookings/': '/bookings',
  '/clients/': '/clients',
  '/my-jobs/': '/my-day',
  '/admin': '/operations',
};

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_dcdf0d5b-e3c7-46d7-864d-f9a117f62290/artifacts/q3fbs21n_Logo.png';

// Helper to determine if current page is a root page
const isRootPage = (pathname) => {
  return ROOT_PAGES.includes(pathname) || pathname === '/';
};

// Helper to get fallback route for back navigation
const getFallbackRoute = (pathname) => {
  for (const [prefix, fallback] of Object.entries(FALLBACK_ROUTES)) {
    if (pathname.startsWith(prefix)) {
      return fallback;
    }
  }
  return '/dashboard';
};

const Layout = ({ children }) => {
  const { user, logout, isDirector, isManager, isAdmin, isStaff } = useAuth();
  const { unreadCount } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Determine if we should show back button
  const showBackButton = !isRootPage(location.pathname);
  
  // Handle back navigation with fallback
  const handleBack = () => {
    // Check if there's browser history
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      // Use fallback route
      navigate(getFallbackRoute(location.pathname));
    }
  };
  
  // Get user display info
  const getUserRole = () => {
    if (isDirector) return 'Director';
    if (isManager) return 'Manager';
    if (isAdmin) return 'Admin';
    if (isStaff) return 'Staff';
    return user?.role || '';
  };

  // Staff-only navigation (simplified)
  const staffNavigation = [
    { name: 'My Day', href: '/my-day', icon: Home },
    { name: 'My Calendar', href: '/calendar', icon: Calendar },
    { name: 'Chat', href: '/chat', icon: MessageSquare },
    { name: 'Chats', href: '/chats', icon: MessagesSquare },
    { name: 'Help', href: '/assistant', icon: HelpCircle },
  ];

  // Manager/Admin navigation - simplified for now
  const adminNavigation = [
    { name: 'Operations', href: '/operations', icon: Home },
    { name: 'Calendar', href: '/calendar', icon: Calendar },
    { name: 'Bookings', href: '/bookings', icon: Briefcase },
    { name: 'Clients', href: '/clients', icon: Users },
    { name: 'Property', href: '/property-lookup', icon: Search },
    { name: 'Admin', href: '/admin', icon: Settings, adminOnly: true },
  ];

  // Determine which navigation to show based on role
  const navigation = (isStaff && !isAdmin) ? staffNavigation : adminNavigation.filter(item => !item.adminOnly || isAdmin);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-card border-r border-border">
          {/* Logo */}
          <div className="flex h-16 shrink-0 items-center px-4 border-b border-border">
            <img src={LOGO_URL} alt="The T Touch" className="h-10 w-auto mix-blend-multiply" />
          </div>

          {/* Navigation */}
          <nav className="flex flex-1 flex-col gap-1 p-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || 
                (item.href !== '/dashboard' && item.href !== '/operations' && location.pathname.startsWith(item.href));
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="h-5 w-5" strokeWidth={1.5} />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User menu */}
          <div className="border-t border-border p-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-between h-auto py-3"
                  data-testid="user-menu-trigger"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium">
                      {user?.full_name?.charAt(0) || 'U'}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-foreground">{user?.full_name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                    </div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="sticky top-0 z-40 flex items-center gap-x-2 bg-card px-3 py-2 shadow-sm md:hidden">
        {/* Menu button - always visible for navigation access */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(true)}
          data-testid="mobile-menu-button"
          className="shrink-0"
        >
          <Menu className="h-5 w-5" />
        </Button>
        
        {/* Back button (shown on non-root pages) */}
        {showBackButton && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            data-testid="mobile-back-button"
            className="shrink-0 flex items-center gap-1 px-2 text-muted-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back</span>
          </Button>
        )}
        
        {/* Spacer */}
        <div className="flex-1" />
        
        {/* User initial badge */}
        <div 
          className="h-7 w-7 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-medium shrink-0"
          data-testid="mobile-user-avatar"
          title={`${user?.full_name} (${getUserRole()})`}
        >
          {user?.full_name?.charAt(0) || 'U'}
        </div>
        
        {/* Notifications */}
        <Link to="/notifications" className="relative shrink-0 p-1.5" data-testid="mobile-notifications-link">
          <Bell className="h-5 w-5 text-muted-foreground" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Link>
        
        {/* Visible Logout button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleLogout}
          className="shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50"
          data-testid="mobile-header-logout-button"
        >
          <LogOut className="h-5 w-5" />
        </Button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-foreground/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-full max-w-xs bg-card">
            <div className="flex h-16 items-center justify-between px-4 border-b border-border">
              <img src={LOGO_URL} alt="The T Touch" className="h-10 w-auto mix-blend-multiply" />
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)}>
                <X className="h-6 w-6" />
              </Button>
            </div>
            <nav className="flex flex-col gap-1 p-4">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-3 text-base font-medium transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent'
                    )}
                  >
                    <item.icon className="h-5 w-5" strokeWidth={1.5} />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
                  {user?.full_name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="font-medium text-foreground">{user?.full_name}</p>
                  <p className="text-sm text-muted-foreground capitalize">{user?.role}</p>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleLogout}
                data-testid="mobile-logout-button"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="md:pl-64">
        {/* Desktop header bar with back, user info, notifications, logout */}
        <div className="hidden md:flex items-center justify-between gap-4 px-6 py-3 bg-card border-b border-border">
          {/* Left side - Back button */}
          <div className="flex items-center gap-3">
            {showBackButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
                data-testid="header-back-button"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back</span>
              </Button>
            )}
          </div>
          
          {/* Right side - User info, notifications, logout */}
          <div className="flex items-center gap-4">
            {/* User label */}
            <div className="flex items-center gap-2 text-sm" data-testid="header-user-info">
              <span className="text-muted-foreground">{user?.full_name}</span>
              <span className="text-muted-foreground/60">({getUserRole()})</span>
            </div>
            
            {/* Notifications */}
            <Link
              to="/notifications"
              className="relative p-2 rounded-lg hover:bg-accent transition-colors"
              data-testid="desktop-notifications-button"
            >
              <Bell className="h-5 w-5 text-muted-foreground" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
            
            {/* Visible Logout button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              data-testid="header-logout-button"
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </Button>
          </div>
        </div>

        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Mobile bottom navigation - Different for staff */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border md:hidden">
        <div className="flex justify-around py-2">
          {(isStaff && !isAdmin) ? (
            // Staff mobile nav
            [
              { name: 'My Day', href: '/my-day', icon: Home },
              { name: 'Calendar', href: '/calendar', icon: Calendar },
              { name: 'Chat', href: '/chat', icon: MessageSquare },
              { name: 'Help', href: '/assistant', icon: HelpCircle },
            ].map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex flex-col items-center gap-1 py-2 px-3 rounded-lg transition-colors',
                    isActive ? 'text-primary' : 'text-muted-foreground'
                  )}
                  data-testid={`mobile-nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className={cn('h-6 w-6', isActive && 'text-primary')} strokeWidth={1.5} />
                  <span className="text-xs font-medium">{item.name}</span>
                </Link>
              );
            })
          ) : (
            // Admin/Manager mobile nav
            [
              { name: 'Home', href: '/operations', icon: Home },
              { name: 'Calendar', href: '/calendar', icon: Calendar },
              { name: 'Bookings', href: '/bookings', icon: Briefcase },
              { name: 'Profile', href: '/settings', icon: Settings },
            ].map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex flex-col items-center gap-1 py-2 px-3 rounded-lg transition-colors',
                    isActive ? 'text-primary' : 'text-muted-foreground'
                  )}
                  data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                >
                  <item.icon className={cn('h-6 w-6', isActive && 'text-primary')} strokeWidth={1.5} />
                  <span className="text-xs font-medium">{item.name}</span>
                </Link>
              );
            })
          )}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
